// PPM payment-and-scheduling status pill (Session AAS, Option C). One labelled pill per PPM
// visit that combines its PAYMENT state (derived from the covering contract invoice) with its
// SCHEDULING state (the job status), so the office can see at a glance which upcoming visits
// have an unpaid or overdue invoice behind them. The pill ALWAYS carries a word, so colour is
// never the only signal (accessibility + the job-type calendar colours stay separate).
//
// Option C keeps every visit `scheduled` (the live calendar is never frozen); the pill is a
// payment-health overlay, not a scheduling gate. The full six-state vocabulary below is kept so
// that if a held/release model is added later, "paid, ready to schedule" is representable.
//
// The overdue rule (confirmed): a still-unpaid visit flips from amber ("Awaiting payment") to
// red ("Payment overdue") once it comes within the runway of its service due date (or its
// covering invoice is already overdue). The runway is a per-tenant tunable setting (Session AAT
// Loop 1): the office endpoint resolves it from TenantConfig and the caller passes it in.
// DEFAULT_RUNWAY_DAYS (14) is the fallback used when no server value is available, so the pill
// behaviour is identical to the pre-config default when unset.

export const DEFAULT_RUNWAY_DAYS = 14;

const DAY_MS = 24 * 60 * 60 * 1000;

// The payment health of the invoice(s) covering a visit's contract:
//   paid    -- the covering invoice is settled (money in)
//   unpaid  -- an outstanding invoice exists (draft/authorised), not yet overdue
//   overdue -- an outstanding invoice is past its due date
//   none    -- no invoice on the contract to assess (do not alarm)
export type PaymentState = "paid" | "unpaid" | "overdue" | "none";

export type PillTone = "green" | "blue" | "amber" | "red" | "grey";

export interface PpmPill {
  label: string; // the word shown on the pill (colour is never the only signal)
  tone: PillTone;
  outline: boolean; // true = office action needed (outline style); false = settled (solid)
}

function toDate(v: Date | string | null | undefined): Date | null {
  if (v == null) return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

// True once a visit is within `runwayDays` of its service due date, OR already past it. Used to
// escalate an unpaid visit to "Payment overdue". A missing/invalid due date is never "within
// runway" (we cannot assert urgency without a date).
export function isWithinServiceRunway(
  scheduledFor: Date | string | null | undefined,
  now: Date,
  runwayDays: number = DEFAULT_RUNWAY_DAYS,
): boolean {
  const due = toDate(scheduledFor);
  if (!due) return false;
  return now.getTime() >= due.getTime() - runwayDays * DAY_MS;
}

// Resolve the single pill for a PPM visit. Terminal scheduling states (complete/cancelled) win;
// otherwise the payment health is overlaid, escalating to red within the service-date runway.
export function ppmPaymentPill(input: {
  jobStatus: string;
  scheduledFor: Date | string | null | undefined;
  paymentState: PaymentState;
  now: Date;
  runwayDays?: number;
}): PpmPill {
  const { jobStatus, scheduledFor, paymentState, now } = input;
  const runwayDays = input.runwayDays ?? DEFAULT_RUNWAY_DAYS;

  // Settled or gone: the scheduling state is final.
  if (jobStatus === "complete") return { label: "Completed", tone: "green", outline: false };
  if (jobStatus === "cancelled") return { label: "Cancelled", tone: "grey", outline: false };

  // An active visit (scheduled / in_progress / pending_approval): overlay the payment health.
  // Paid, or no invoice to chase -> the visit is simply scheduled (settled, solid blue).
  if (paymentState === "paid" || paymentState === "none") {
    return { label: "Scheduled", tone: "blue", outline: false };
  }
  // Outstanding: amber normally, red once overdue or within the service-date runway.
  if (paymentState === "overdue" || isWithinServiceRunway(scheduledFor, now, runwayDays)) {
    return { label: "Payment overdue", tone: "red", outline: true };
  }
  return { label: "Awaiting payment", tone: "amber", outline: true };
}
